import { ChangeEvent } from "react";
import { faUndo } from "@fortawesome/free-solid-svg-icons/faUndo";
import { resources } from "@constants/hardcoded-strings";
import { LabelWidthTypes } from "@enums/label-width-types";
import { LayoutTypes } from "@enums/layout-types";
import { PageLevelTypes } from "@enums/page-level-types";
import { AXNUIContent, AXNUIContentFormAsyncInput, AXNUIContentFormImageAttribute } from "@iddataweb/idw-common-ui/types";
import { ThemingFormContentValidationErrors } from "@idw-types/common/validation/common/theming-validation-errors";
import { InputFormGroup } from "@containers/common/input-form-group/input-form-group";
import { TextareaFormGroup } from "@containers/common/textarea-form-group/textarea-form-group";
import { ThemingFormGroupWrapper } from "@containers/common/theming-input-form-group/theming-form-group-wrapper";

type ThemingFormContentDocumentCaptureSectionProps = {
    layout: LayoutTypes;
    labelWidth: LabelWidthTypes;
    isDisabled: boolean;
    validationErrors: ThemingFormContentValidationErrors;
    inheritedAXNUIContent: AXNUIContent;
    axnuiContent: AXNUIContent;
    pageLevel: PageLevelTypes;
    updateAXNUIContentFormAsyncInputFieldValue: (key: keyof AXNUIContentFormAsyncInput, value: string) => void;
    updateAXNUIContentFormImageAttributeFieldValue: (key: keyof AXNUIContentFormImageAttribute, value: string) => void;
};

export const ThemingFormContentDocumentCaptureSection = (props: ThemingFormContentDocumentCaptureSectionProps) => {
    const {
        layout,
        labelWidth,
        isDisabled,
        validationErrors,
        inheritedAXNUIContent,
        axnuiContent,
        pageLevel,
        updateAXNUIContentFormAsyncInputFieldValue,
        updateAXNUIContentFormImageAttributeFieldValue
    } = props;

    const handleCaptureMessageChange = async (event: ChangeEvent<HTMLInputElement>) => {
        updateAXNUIContentFormAsyncInputFieldValue("captureMessage", event.target.value);
    };

    const handleResetCaptureMessage = () => {
        updateAXNUIContentFormAsyncInputFieldValue("captureMessage", inheritedAXNUIContent.form.asyncInput.captureMessage);
    };

    const handleStatusTextChange = async (event: ChangeEvent<HTMLInputElement>) => {
        updateAXNUIContentFormAsyncInputFieldValue("status", event.target.value);
    };

    const handleResetStatusText = () => {
        updateAXNUIContentFormAsyncInputFieldValue("status", inheritedAXNUIContent.form.asyncInput.status);
    };

    const handleCompleteStatusTextChange = async (event: ChangeEvent<HTMLInputElement>) => {
        updateAXNUIContentFormAsyncInputFieldValue("completeStatus", event.target.value);
    };

    const handleResetCompleteStatusText = () => {
        updateAXNUIContentFormAsyncInputFieldValue("completeStatus", inheritedAXNUIContent.form.asyncInput.completeStatus);
    };

    const handleIncompleteStatusTextChange = async (event: ChangeEvent<HTMLInputElement>) => {
        updateAXNUIContentFormAsyncInputFieldValue("incompleteStatus", event.target.value);
    };

    const handleResetIncompleteStatusText = () => {
        updateAXNUIContentFormAsyncInputFieldValue("incompleteStatus", inheritedAXNUIContent.form.asyncInput.incompleteStatus);
    };

    const handleFailedStatusTextChange = async (event: ChangeEvent<HTMLInputElement>) => {
        updateAXNUIContentFormAsyncInputFieldValue("failedStatus", event.target.value);
    };

    const handleResetFailedStatusText = () => {
        updateAXNUIContentFormAsyncInputFieldValue("failedStatus", inheritedAXNUIContent.form.asyncInput.failedStatus);
    };

    const handleUnableToVerifyStatusTextChange = async (event: ChangeEvent<HTMLInputElement>) => {
        updateAXNUIContentFormAsyncInputFieldValue("unableToVerifyStatus", event.target.value);
    };

    const handleResetUnableToVerifyStatusText = () => {
        updateAXNUIContentFormAsyncInputFieldValue("unableToVerifyStatus", inheritedAXNUIContent.form.asyncInput.unableToVerifyStatus);
    };

    const handlePreviewTextSingleChange = async (event: ChangeEvent<HTMLInputElement>) => {
        updateAXNUIContentFormImageAttributeFieldValue("previewTextSingle", event.target.value);
    };

    const handleResetPreviewTextSingle = () => {
        updateAXNUIContentFormImageAttributeFieldValue("previewTextSingle", inheritedAXNUIContent.form.imageAttribute.previewTextSingle);
    };

    const handlePreviewTextMultipleChange = async (event: ChangeEvent<HTMLInputElement>) => {
        updateAXNUIContentFormImageAttributeFieldValue("previewTextMultiple", event.target.value);
    };

    const handleResetPreviewTextMultiple = () => {
        updateAXNUIContentFormImageAttributeFieldValue("previewTextMultiple", inheritedAXNUIContent.form.imageAttribute.previewTextMultiple);
    };

    return (
        <div>
            <ThemingFormGroupWrapper
                inheritedThemingPropertyValue={inheritedAXNUIContent.form.asyncInput.captureMessage}
                currentThemingPropertyValue={axnuiContent.form.asyncInput.captureMessage}
                pageLevel={pageLevel}
            >
                <TextareaFormGroup
                    controlId={"theming-form-content-capture-message-input"}
                    automationId={"theming-form-content-capture-message-input"}
                    label={resources.pages.verificationServiceDetails.tabs.theming.subTabs.formContent.sections.documentCapturePlaceholder.inputs.captureMessage.label}
                    ariaLabel={resources.pages.verificationServiceDetails.tabs.theming.subTabs.formContent.sections.documentCapturePlaceholder.inputs.captureMessage.label}
                    layout={layout}
                    labelWidth={labelWidth}
                    onChange={handleCaptureMessageChange}
                    value={axnuiContent.form.asyncInput.captureMessage || ""}
                    disabled={isDisabled}
                    isRequired={true}
                    afterLabelTooltipText={resources.pages.verificationServiceDetails.tabs.theming.subTabs.formContent.sections.documentCapturePlaceholder.inputs.captureMessage.tooltip}
                    afterLabelIcon={isDisabled ? null : faUndo}
                    afterLabelIconOnClick={handleResetCaptureMessage}
                />
            </ThemingFormGroupWrapper>
            {validationErrors.captureMessageError}
            <ThemingFormGroupWrapper
                inheritedThemingPropertyValue={inheritedAXNUIContent.form.asyncInput.status}
                currentThemingPropertyValue={axnuiContent.form.asyncInput.status}
                pageLevel={pageLevel}
            >
                <InputFormGroup
                    controlId={"theming-form-content-status-text-input"}
                    automationId={"theming-form-content-status-text-input"}
                    label={resources.pages.verificationServiceDetails.tabs.theming.subTabs.formContent.sections.documentCapturePlaceholder.inputs.statusText.label}
                    ariaLabel={resources.pages.verificationServiceDetails.tabs.theming.subTabs.formContent.sections.documentCapturePlaceholder.inputs.statusText.label}
                    layout={layout}
                    labelWidth={labelWidth}
                    onChange={handleStatusTextChange}
                    value={axnuiContent.form.asyncInput.status || ""}
                    disabled={isDisabled}
                    isRequired={true}
                    afterLabelTooltipText={resources.pages.verificationServiceDetails.tabs.theming.subTabs.formContent.sections.documentCapturePlaceholder.inputs.statusText.tooltip}
                    afterLabelIcon={isDisabled ? null : faUndo}
                    afterLabelIconOnClick={handleResetStatusText}
                />
            </ThemingFormGroupWrapper>
            {validationErrors.statusTextError}
            <ThemingFormGroupWrapper
                inheritedThemingPropertyValue={inheritedAXNUIContent.form.asyncInput.completeStatus}
                currentThemingPropertyValue={axnuiContent.form.asyncInput.completeStatus}
                pageLevel={pageLevel}
            >
                <InputFormGroup
                    controlId={"theming-form-content-complete-status-text-input"}
                    automationId={"theming-form-content-complete-status-text-input"}
                    label={resources.pages.verificationServiceDetails.tabs.theming.subTabs.formContent.sections.documentCapturePlaceholder.inputs.completeStatusText.label}
                    ariaLabel={resources.pages.verificationServiceDetails.tabs.theming.subTabs.formContent.sections.documentCapturePlaceholder.inputs.completeStatusText.label}
                    layout={layout}
                    labelWidth={labelWidth}
                    onChange={handleCompleteStatusTextChange}
                    value={axnuiContent.form.asyncInput.completeStatus || ""}
                    disabled={isDisabled}
                    isRequired={true}
                    afterLabelTooltipText={resources.pages.verificationServiceDetails.tabs.theming.subTabs.formContent.sections.documentCapturePlaceholder.inputs.completeStatusText.tooltip}
                    afterLabelIcon={isDisabled ? null : faUndo}
                    afterLabelIconOnClick={handleResetCompleteStatusText}
                />
            </ThemingFormGroupWrapper>
            {validationErrors.completeStatusTextError}
            <ThemingFormGroupWrapper
                inheritedThemingPropertyValue={inheritedAXNUIContent.form.asyncInput.incompleteStatus}
                currentThemingPropertyValue={axnuiContent.form.asyncInput.incompleteStatus}
                pageLevel={pageLevel}
            >
                <InputFormGroup
                    controlId={"theming-form-content-incomplete-status-text-input"}
                    automationId={"theming-form-content-incomplete-status-text-input"}
                    label={resources.pages.verificationServiceDetails.tabs.theming.subTabs.formContent.sections.documentCapturePlaceholder.inputs.incompleteStatusText.label}
                    ariaLabel={resources.pages.verificationServiceDetails.tabs.theming.subTabs.formContent.sections.documentCapturePlaceholder.inputs.incompleteStatusText.label}
                    layout={layout}
                    labelWidth={labelWidth}
                    onChange={handleIncompleteStatusTextChange}
                    value={axnuiContent.form.asyncInput.incompleteStatus || ""}
                    disabled={isDisabled}
                    isRequired={true}
                    afterLabelTooltipText={resources.pages.verificationServiceDetails.tabs.theming.subTabs.formContent.sections.documentCapturePlaceholder.inputs.incompleteStatusText.tooltip}
                    afterLabelIcon={isDisabled ? null : faUndo}
                    afterLabelIconOnClick={handleResetIncompleteStatusText}
                />
            </ThemingFormGroupWrapper>
            {validationErrors.incompleteStatusTextError}
            <ThemingFormGroupWrapper
                inheritedThemingPropertyValue={inheritedAXNUIContent.form.asyncInput.failedStatus}
                currentThemingPropertyValue={axnuiContent.form.asyncInput.failedStatus}
                pageLevel={pageLevel}
            >
                <InputFormGroup
                    controlId={"theming-form-content-failed-status-text-input"}
                    automationId={"theming-form-content-failed-status-text-input"}
                    label={resources.pages.verificationServiceDetails.tabs.theming.subTabs.formContent.sections.documentCapturePlaceholder.inputs.failedStatusText.label}
                    ariaLabel={resources.pages.verificationServiceDetails.tabs.theming.subTabs.formContent.sections.documentCapturePlaceholder.inputs.failedStatusText.label}
                    layout={layout}
                    labelWidth={labelWidth}
                    onChange={handleFailedStatusTextChange}
                    value={axnuiContent.form.asyncInput.failedStatus || ""}
                    disabled={isDisabled}
                    isRequired={true}
                    afterLabelTooltipText={resources.pages.verificationServiceDetails.tabs.theming.subTabs.formContent.sections.documentCapturePlaceholder.inputs.failedStatusText.tooltip}
                    afterLabelIcon={isDisabled ? null : faUndo}
                    afterLabelIconOnClick={handleResetFailedStatusText}
                />
            </ThemingFormGroupWrapper>
            {validationErrors.failedStatusTextError}
            <ThemingFormGroupWrapper
                inheritedThemingPropertyValue={inheritedAXNUIContent.form.asyncInput.unableToVerifyStatus}
                currentThemingPropertyValue={axnuiContent.form.asyncInput.unableToVerifyStatus}
                pageLevel={pageLevel}
            >
                <InputFormGroup
                    controlId={"theming-form-content-unable-to-verify-status-text-input"}
                    automationId={"theming-form-content-unable-to-verify-status-text-input"}
                    label={resources.pages.verificationServiceDetails.tabs.theming.subTabs.formContent.sections.documentCapturePlaceholder.inputs.unableToVerifyStatusText.label}
                    ariaLabel={resources.pages.verificationServiceDetails.tabs.theming.subTabs.formContent.sections.documentCapturePlaceholder.inputs.unableToVerifyStatusText.label}
                    layout={layout}
                    labelWidth={labelWidth}
                    onChange={handleUnableToVerifyStatusTextChange}
                    value={axnuiContent.form.asyncInput.unableToVerifyStatus || ""}
                    disabled={isDisabled}
                    isRequired={true}
                    afterLabelTooltipText={resources.pages.verificationServiceDetails.tabs.theming.subTabs.formContent.sections.documentCapturePlaceholder.inputs.unableToVerifyStatusText.tooltip}
                    afterLabelIcon={isDisabled ? null : faUndo}
                    afterLabelIconOnClick={handleResetUnableToVerifyStatusText}
                />
            </ThemingFormGroupWrapper>
            {validationErrors.unableToVerifyStatusTextError}
            <ThemingFormGroupWrapper
                inheritedThemingPropertyValue={inheritedAXNUIContent.form.imageAttribute.previewTextSingle}
                currentThemingPropertyValue={axnuiContent.form.imageAttribute.previewTextSingle}
                pageLevel={pageLevel}
            >
                <TextareaFormGroup
                    controlId={"theming-form-content-preview-text-single-input"}
                    automationId={"theming-form-content-preview-text-single-input"}
                    label={resources.pages.verificationServiceDetails.tabs.theming.subTabs.formContent.sections.documentCapturePlaceholder.inputs.previewTextSingle.label}
                    ariaLabel={resources.pages.verificationServiceDetails.tabs.theming.subTabs.formContent.sections.documentCapturePlaceholder.inputs.previewTextSingle.label}
                    layout={layout}
                    labelWidth={labelWidth}
                    onChange={handlePreviewTextSingleChange}
                    value={axnuiContent.form.imageAttribute.previewTextSingle || ""}
                    disabled={isDisabled}
                    isRequired={true}
                    afterLabelTooltipText={resources.pages.verificationServiceDetails.tabs.theming.subTabs.formContent.sections.documentCapturePlaceholder.inputs.previewTextSingle.tooltip}
                    afterLabelIcon={isDisabled ? null : faUndo}
                    afterLabelIconOnClick={handleResetPreviewTextSingle}
                />
            </ThemingFormGroupWrapper>
            {validationErrors.previewTextSingleError}
            <ThemingFormGroupWrapper
                inheritedThemingPropertyValue={inheritedAXNUIContent.form.imageAttribute.previewTextMultiple}
                currentThemingPropertyValue={axnuiContent.form.imageAttribute.previewTextMultiple}
                pageLevel={pageLevel}
            >
                <TextareaFormGroup
                    controlId={"theming-form-content-preview-text-multiple-input"}
                    automationId={"theming-form-content-preview-text-multiple-input"}
                    label={resources.pages.verificationServiceDetails.tabs.theming.subTabs.formContent.sections.documentCapturePlaceholder.inputs.previewTextMultiple.label}
                    ariaLabel={resources.pages.verificationServiceDetails.tabs.theming.subTabs.formContent.sections.documentCapturePlaceholder.inputs.previewTextMultiple.label}
                    layout={layout}
                    labelWidth={labelWidth}
                    onChange={handlePreviewTextMultipleChange}
                    value={axnuiContent.form.imageAttribute.previewTextMultiple || ""}
                    disabled={isDisabled}
                    isRequired={true}
                    afterLabelTooltipText={resources.pages.verificationServiceDetails.tabs.theming.subTabs.formContent.sections.documentCapturePlaceholder.inputs.previewTextMultiple.tooltip}
                    afterLabelIcon={isDisabled ? null : faUndo}
                    afterLabelIconOnClick={handleResetPreviewTextMultiple}
                />
            </ThemingFormGroupWrapper>
            {validationErrors.previewTextMultipleError}
        </div>
    );
};